import { useEffect, useState } from 'preact/hooks';
import { useAuth, useUser, UserButton } from '@clerk/clerk-react';
import { route } from 'preact-router';
import { api, ApiError, EngagementReport, SessionSummaryData, ReflectionMetadataData, ReflectionDecisionType, ApplicationRecord, ApplicationStatus } from '../api/client';
import { profileApi, type ProfileDepth, type Profile as ApiProfile } from '../api/profile';
import { DashboardSkeleton, SkeletonStyles } from '../components/Skeleton';
import { GuideCompanion, type GuideUserState } from '../components/GuideCompanion';
import { PublishPreviewModal } from '../components/PublishPreviewModal';

interface UserInfo {
	id: string;
	email: string;
	tier: 'free' | 'pro';
	emailVerified: boolean;
	interviewsRemaining: number;
	canStartInterview: boolean;
	activeSessionId: string | null;
}

interface SessionSummary {
	id: string;
	type: string;
	mode?: 'canonical' | 'private';
	status: string;
	startedAt: number;
	lastActiveAt: number;
	messageCount: number;
	pendingClaimCount: number;
	sessionSummary?: SessionSummaryData | null;
	reflectionMetadata?: ReflectionMetadataData | null;
}

function sessionLabel(session: SessionSummary): string {
	if (session.mode === 'private') return 'Private Reflection';
	// Legacy coaching sessions (2 abandoned historical rows) — display generically
	if (session.type === 'coaching') return 'Legacy Session';
	return 'Record Session';
}

const DECISION_TYPE_LABELS: Record<ReflectionDecisionType, string> = {
	interview_processing: 'Interview processing',
	role_fit: 'Role fit',
	pivot_consideration: 'Pivot consideration',
	comp_review: 'Compensation',
	relationship: 'Workplace dynamics',
	learning: 'Learning',
	other: 'Reflection',
};

function decisionTypeLabel(meta: ReflectionMetadataData | null | undefined): string | null {
	if (!meta) return null;
	if (meta.decisionType === 'other') {
		return meta.decisionTypeOther && meta.decisionTypeOther.trim().length > 0
			? meta.decisionTypeOther
			: DECISION_TYPE_LABELS.other;
	}
	return DECISION_TYPE_LABELS[meta.decisionType];
}

type SessionModeFilter = 'all' | 'record' | 'private';

interface Profile {
	id: string;
	slug: string;
	displayName: string;
	isPublic: boolean;
	publishedAt: number | null;
	chatEnabled: boolean;
	resumeUrl: string | null;
	linkedinUrl: string | null;
	portfolioUrl: string | null;
}

const FULL_CARD_COUNT = 2;

export function Dashboard() {
	const { getToken } = useAuth();
	const { user: clerkUser } = useUser();
	const [user, setUser] = useState<UserInfo | null>(null);
	const [sessions, setSessions] = useState<SessionSummary[]>([]);
	const [reports, setReports] = useState<EngagementReport[]>([]);
	const [applications, setApplications] = useState<ApplicationRecord[]>([]);
	const [profile, setProfile] = useState<Profile | null>(null);
	const [profileDepth, setProfileDepth] = useState<ProfileDepth | null>(null);
	const [loading, setLoading] = useState(true);
	const [starting, setStarting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [showAllSessions, setShowAllSessions] = useState(false);
	const [sessionModeFilter, setSessionModeFilter] = useState<SessionModeFilter>('all');
	const [showAllReports, setShowAllReports] = useState(false);
	const [expandedReports, setExpandedReports] = useState<Record<string, boolean>>({});
	const [expandedApps, setExpandedApps] = useState<Record<string, boolean>>({});
	const [editingNotes, setEditingNotes] = useState<Record<string, string>>({});
	const [savingApp, setSavingApp] = useState<string | null>(null);
	const [showPublishPreview, setShowPublishPreview] = useState(false);
	const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
	const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'annual'>('monthly');
	const [upgrading, setUpgrading] = useState(false);
	const [headerCompact, setHeaderCompact] = useState(false);

	useEffect(() => {
		loadData();
	}, []);

	useEffect(() => {
		const onScroll = () => setHeaderCompact(window.scrollY > 40);
		window.addEventListener('scroll', onScroll, { passive: true });
		return () => window.removeEventListener('scroll', onScroll);
	}, []);

	async function loadData() {
		try {
			setLoading(true);
			const token = await getToken();
			if (!token) return;

			const [userRes, sessionsRes, profileRes] = await Promise.all([
				api.getMe(token),
				api.listSessions(token),
				profileApi.getProfile(token),
			]);

			setUser(userRes);
			setSessions(sessionsRes.sessions);
			if (profileRes.profile) {
				setProfile(profileRes.profile);
			}
			if (profileRes.depth) {
				setProfileDepth(profileRes.depth);
			}


			// Fetch engagement reports for all users (free sees teaser, pro sees full)
			const reportsRes = await api.getReports(token).catch(() => ({ reports: [] }));
			setReports(reportsRes.reports);

			// Applications are Pro-only
			if (userRes.tier === 'pro') {
				const appsRes = await api.getApplications(token).catch(() => ({ applications: [] }));
				setApplications(appsRes.applications);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to load data');
		} finally {
			setLoading(false);
		}
	}

	async function startInterview() {
		try {
			setStarting(true);
			setError(null);
			const token = await getToken();
			if (!token) return;

			const result = await api.startInterview(token, { type: 'canonical' });
			route(`/interview/${result.sessionId}`);
		} catch (err) {
			if (err instanceof ApiError && err.upgradeRequired) {
				setShowUpgradePrompt(true);
			} else {
				setError(err instanceof Error ? err.message : 'Failed to start interview');
			}
			setStarting(false);
		}
	}

	async function startPrivateReflection() {
		try {
			setStarting(true);
			setError(null);
			const token = await getToken();
			if (!token) return;

			const result = await api.startInterview(token, { type: 'reflection', mode: 'private' });
			route(`/interview/${result.sessionId}`);
		} catch (err) {
			if (err instanceof ApiError && err.upgradeRequired) {
				setShowUpgradePrompt(true);
			} else {
				setError(err instanceof Error ? err.message : 'Failed to start private reflection');
			}
			setStarting(false);
		}
	}

	async function handleUpgrade() {
		if (upgrading) return;
		try {
			setUpgrading(true);
			const token = await getToken();
			if (!token) return;
			const { url } = await api.createCheckoutSession(token, selectedPlan);
			window.location.href = url;
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to start checkout');
			setShowUpgradePrompt(false);
			setUpgrading(false);
		}
	}

	async function abandonSession(sessionId: string, e: Event) {
		e.stopPropagation();
		if (!confirm('Remove this session? Approved claims are kept.')) return;
		try {
			const token = await getToken();
			if (!token) return;
			await api.abandonSession(token, sessionId);
			setSessions(prev => prev.filter(s => s.id !== sessionId));
			// Clear active session if this was it
			if (user?.activeSessionId === sessionId) {
				setUser(prev => prev ? { ...prev, activeSessionId: null } : prev);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to remove session');
		}
	}

	async function updateApplicationStatus(appId: string, status: ApplicationStatus) {
		try {
			setSavingApp(appId);
			const token = await getToken();
			if (!token) return;
			await api.updateApplication(token, appId, { status });
			setApplications(prev => prev.map(a =>
				a.id === appId ? { ...a, status, updatedAt: Date.now() } : a
			));
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to update application');
		} finally {
			setSavingApp(null);
		}
	}

	async function saveApplicationNotes(appId: string) {
		const notes = editingNotes[appId];
		if (notes === undefined) return;
		try {
			setSavingApp(appId);
			const token = await getToken();
			if (!token) return;
			await api.updateApplication(token, appId, { notes });
			setApplications(prev => prev.map(a =>
				a.id === appId ? { ...a, notes, updatedAt: Date.now() } : a
			));
			setEditingNotes(prev => {
				const next = { ...prev };
				delete next[appId];
				return next;
			});
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to save notes');
		} finally {
			setSavingApp(null);
		}
	}

	function handlePublished(result: { slug: string }) {
		setProfile(prev => prev ? { ...prev, isPublic: true, publishedAt: Date.now(), slug: result.slug } : prev);
		setShowPublishPreview(false);
	}



	// Profile is stale when the most recent session activity is after last publish
	const lastSessionActivity = sessions.length > 0
		? Math.max(...sessions.map(s => s.lastActiveAt))
		: 0;
	const profileIsStale = profile?.isPublic && profile?.publishedAt
		? lastSessionActivity > profile.publishedAt
		: false;

	const hasCompletedExtraction = sessions.some(
		s => s.type === 'canonical' && s.status === 'completed'
	);

	const completedSessionCount = sessions.filter(
		s => s.type === 'canonical' && s.status === 'completed'
	).length;

	// Can the user actually publish? Below the representation floor
	// (no identity, or no substantive work), no. Above it — even at
	// thin depth — yes. The guide companion handles honesty about
	// thinness; the button just enforces the floor.
	const canPublish = profileDepth ? profileDepth.canRepresent : false;
	const floorHint = profileDepth?.floorReasons[0] ?? '';

	const guideState: GuideUserState | undefined = user ? {
		tier: user.tier,
		emailVerified: user.emailVerified,
		hasCompletedExtraction,
		sessionCount: sessions.length,
		completedSessionCount,
		isPublished: !!profile?.isPublic,
		profileIsStale,
		hasActiveSession: !!user.activeSessionId,
		canStartInterview: user.canStartInterview,
		profileDepth: profileDepth ?? null,
	} : undefined;

	function formatDate(timestamp: number): string {
		return new Date(timestamp).toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			hour: 'numeric',
			minute: '2-digit',
		});
	}

	if (loading) {
		return (
			<div class="page">
				<header class="dashboard-header">
					<div class="container">
						<nav class="dashboard-nav">
							<a href="/" class="logo">
								<span class="logo-text">pxtxt</span>
							</a>
							<div class="nav-actions">
								<UserButton afterSignOutUrl="/" />
							</div>
						</nav>
					</div>
				</header>
				<main class="dashboard-main">
					<div class="container">
						<SkeletonStyles />
						<DashboardSkeleton />
					</div>
				</main>
				<style>{dashboardStyles}</style>
			</div>
		);
	}

	return (
		<div class="page">
			<header class={`dashboard-header ${headerCompact ? 'dashboard-header--compact' : ''}`}>
				<div class="container">
					<nav class="dashboard-nav">
						<a href="/" class="logo">
							<span class="logo-text">pxtxt</span>
						</a>
						<div class="nav-actions">
							{user?.tier === 'pro' && (
								<span class="badge badge-primary">Subscribed</span>
							)}
							<UserButton afterSignOutUrl="/" />
						</div>
					</nav>
				</div>
			</header>

			<main class="dashboard-main">
				<div class="container">
					<div class="dashboard-welcome">
						<h1 class="t-title">Dashboard</h1>
						<p class="t-body text-muted">
							Build your professional narrative through conversation.
						</p>
					</div>

					{error && (
						<div class="error-banner">
							<span>{error}</span>
							<button class="error-dismiss" onClick={() => setError(null)}>
								<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
									<path d="M18 6L6 18M6 6l12 12" />
								</svg>
							</button>
						</div>
					)}

					{user && !user.emailVerified && (
						<div class="verification-banner">
							<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
								<path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
								<line x1="12" y1="9" x2="12" y2="13" />
								<line x1="12" y1="17" x2="12.01" y2="17" />
							</svg>
							<span>Please verify your email address to start interviews. Check your inbox for a verification link.</span>
						</div>
					)}

					{/* Profile & Record */}
					<div class="profile-section" data-guide-section="profile">
						<div class="profile-column">
							{/* YOUR PROFILE — status card */}
							<div class="section-header">
								<h2 class="t-label">Your Profile</h2>
								<a href="/settings" class="profile-settings-btn" title="Settings">
									<svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
										<path d="M9.39797 13.4749C9.39797 13.7648 9.16274 14 8.87282 14H7.12746C6.83751 14 6.60231 13.7648 6.60231 13.4749L6.60231 11.9624C6.60231 11.7553 6.7702 11.5874 6.97731 11.5874C7.18442 11.5874 7.35231 11.7553 7.35231 11.9624L7.35231 13.25H8.64797V11.9624C8.64797 11.7553 8.81586 11.5874 9.02297 11.5874C9.22998 11.5875 9.39797 11.7554 9.39797 11.9624V13.4749ZM9.39797 4.03833C9.39778 4.24522 9.22987 4.41322 9.02297 4.41333C8.81597 4.41333 8.64815 4.24528 8.64797 4.03833V2.75H7.35231V4.03833C7.35213 4.24528 7.18431 4.41333 6.97731 4.41333C6.77032 4.41333 6.60249 4.24528 6.60231 4.03833V2.52515C6.60231 2.2352 6.83751 2 7.12746 2L8.87282 2C9.16274 2.00003 9.39797 2.23521 9.39797 2.52515V4.03833Z" fill="currentColor"/>
										<path d="M5.11721 12.8598C4.9122 13.0648 4.57957 13.0647 4.37454 12.8598L3.14039 11.6256C2.93536 11.4206 2.93536 11.088 3.14039 10.8829L4.20985 9.81346C4.3563 9.66702 4.59374 9.66702 4.74018 9.81346C4.88663 9.95991 4.88663 10.1973 4.74018 10.3438L3.82971 11.2543L4.74588 12.1704L5.65635 11.26C5.80279 11.1135 6.04023 11.1135 6.18668 11.26C6.33298 11.4064 6.33308 11.6439 6.18668 11.7903L5.11721 12.8598ZM11.7898 6.18712C11.6434 6.33329 11.4059 6.33335 11.2595 6.18712C11.1131 6.04076 11.1133 5.80326 11.2595 5.65679L12.1705 4.74581L11.2543 3.82964L10.3433 4.74063C10.1969 4.88684 9.95939 4.88699 9.81302 4.74063C9.66665 4.59426 9.66681 4.35676 9.81302 4.2103L10.883 3.14032C11.088 2.93529 11.4206 2.93529 11.6257 3.14032L12.8598 4.37447C13.0648 4.5795 13.0648 4.91213 12.8598 5.11714L11.7898 6.18712Z" fill="currentColor"/>
										<path d="M2.52515 9.39802C2.23521 9.39802 2.00003 9.1628 2 8.87288L2 7.12752C2 6.83757 2.2352 6.60237 2.52515 6.60237H4.0376C4.2447 6.60237 4.4126 6.77026 4.4126 6.97737C4.4126 7.18448 4.2447 7.35237 4.0376 7.35237H2.75V8.64802H4.0376C4.2447 8.64802 4.4126 8.81592 4.4126 9.02302C4.41249 9.23004 4.24464 9.39802 4.0376 9.39802H2.52515ZM11.9617 9.39802C11.7548 9.39784 11.5868 9.22993 11.5867 9.02302C11.5867 8.81603 11.7547 8.64821 11.9617 8.64802H13.25V7.35237H11.9617C11.7547 7.35219 11.5867 7.18436 11.5867 6.97737C11.5867 6.77038 11.7547 6.60255 11.9617 6.60237H13.4749C13.7648 6.60237 14 6.83757 14 7.12752V8.87288C14 9.1628 13.7648 9.39802 13.4749 9.39802H11.9617Z" fill="currentColor"/>
										<path d="M3.14019 5.11711C2.93518 4.91209 2.93521 4.57946 3.14019 4.37444L4.37435 3.14028C4.57937 2.93526 4.91199 2.93525 5.11702 3.14028L6.18648 4.20974C6.33293 4.35619 6.33293 4.59363 6.18648 4.74007C6.04004 4.88652 5.8026 4.88652 5.65615 4.74007L4.74568 3.82961L3.82952 4.74577L4.73999 5.65624C4.88643 5.80269 4.88643 6.04012 4.73999 6.18657C4.59353 6.33288 4.35606 6.33297 4.20966 6.18657L3.14019 5.11711ZM9.81282 11.7897C9.66666 11.6433 9.6666 11.4058 9.81282 11.2594C9.95919 11.113 10.1967 11.1132 10.3432 11.2594L11.2541 12.1704L12.1703 11.2542L11.2593 10.3432C11.1131 10.1968 11.113 9.95928 11.2593 9.81291C11.4057 9.66654 11.6432 9.6667 11.7896 9.81291L12.8596 10.8829C13.0647 11.0879 13.0647 11.4205 12.8596 11.6256L11.6255 12.8597C11.4204 13.0647 11.0878 13.0647 10.8828 12.8597L9.81282 11.7897Z" fill="currentColor"/>
										<path d="M6.11227 11.2017C6.41504 11.3805 6.73789 11.5128 7.07039 11.5984L6.88394 12.3245C6.48424 12.2217 6.09561 12.0632 5.7311 11.848L6.11227 11.2017ZM12.3245 9.11608C12.2217 9.51589 12.0628 9.90485 11.8475 10.2694L11.5249 10.0783L11.2017 9.88775C11.3805 9.58497 11.5129 9.26213 11.5984 8.92963L12.3245 9.11608ZM8.92965 11.5984C9.26214 11.5128 9.585 11.3805 9.88777 11.2017L10.2689 11.848C9.90443 12.0632 9.5158 12.2217 9.1161 12.3245L8.92965 11.5984ZM11.5249 5.92167L11.8475 5.73056C12.0628 6.09516 12.2217 6.4841 12.3245 6.88392L11.5984 7.07037C11.5129 6.73786 11.3805 6.41503 11.2017 6.11225L11.5249 5.92167ZM4.40165 8.92963C4.48718 9.26212 4.61955 9.58498 4.79836 9.88775L4.15202 10.2689C3.93677 9.90441 3.77834 9.51578 3.67555 9.11608L4.40165 8.92963ZM9.1161 3.67553C9.5158 3.77832 9.90444 3.93675 10.2689 4.152L9.88777 4.79834C9.585 4.61953 9.26214 4.48716 8.92965 4.40163L9.1161 3.67553ZM4.15202 5.73108C4.2573 5.55283 4.48748 5.49336 4.66578 5.5985C4.84406 5.70379 4.90357 5.93394 4.79836 6.11225C4.61954 6.41502 4.48718 6.73787 4.40165 7.07037L3.67555 6.88392C3.77834 6.48422 3.93677 6.09559 4.15202 5.73108ZM5.73058 4.15252C6.09518 3.93718 6.48413 3.77835 6.88394 3.67553L7.07039 4.40163C6.73788 4.48716 6.41505 4.61952 6.11227 4.79834C5.93396 4.90357 5.70381 4.84405 5.59852 4.66576C5.49344 4.48756 5.55257 4.25788 5.73058 4.15252Z" fill="currentColor"/>
										<path d="M9.5 8C9.5 7.17157 8.82843 6.5 8 6.5C7.17157 6.5 6.5 7.17157 6.5 8C6.5 8.82843 7.17157 9.5 8 9.5V10.25C6.75736 10.25 5.75 9.24264 5.75 8C5.75 6.75736 6.75736 5.75 8 5.75C9.24264 5.75 10.25 6.75736 10.25 8C10.25 9.24264 9.24264 10.25 8 10.25V9.5C8.82843 9.5 9.5 8.82843 9.5 8Z" fill="currentColor"/>
									</svg>
								</a>
							</div>
							<div class="avatar-card">
								{profile?.isPublic ? (
									<span class="avatar-card__badge badge badge-success">Live</span>
								) : hasCompletedExtraction ? (
									<span class="avatar-card__badge badge badge-default">Unpublished</span>
								) : null}
								<div class="profile-avatar-lg">
									{clerkUser?.imageUrl ? (
										<img src={clerkUser.imageUrl} alt="" class="profile-avatar-img" />
									) : (
										<span class="profile-avatar-initial">Upload</span>
									)}
								</div>
								{profile?.isPublic && profile?.slug ? (
									<>
										<div class="profile-url-row">
											<a href={`/b/${profile.slug}`} class="profile-url" target="_blank" rel="noopener noreferrer">
												pxtxt.ai/b/{profile.slug}
											</a>
											<a href="/settings" class="profile-info-icon" title="Manage publishing in Settings">
												<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
													<circle cx="12" cy="12" r="10" />
													<path d="M12 16v-4" />
													<path d="M12 8h.01" />
												</svg>
											</a>
										</div>
										{profileIsStale && (
											<p class="t-caption profile-stale-hint">Record updated since last publish</p>
										)}
										<a
											href="#"
											class="profile-update-link"
											onClick={(e: Event) => { e.preventDefault(); setShowPublishPreview(true); }}
										>
											{profileIsStale ? 'Update Profile' : 'Republish'}
										</a>
									</>
								) : hasCompletedExtraction ? (
									canPublish ? (
										<a
											href="#"
											class="profile-publish-link"
											onClick={(e: Event) => { e.preventDefault(); setShowPublishPreview(true); }}
										>
											Publish Profile
											<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
												<path d="M12 19V5M5 12l7-7 7 7" />
											</svg>
										</a>
									) : (
										<p class="t-caption text-muted" style="margin: 0; text-align: center;">
											{floorHint || 'A bit more in your Record and I can represent you.'}
										</p>
									)
								) : (
									<p class="t-caption text-muted" style="margin: 0; text-align: center;">
										Your Profile starts with a conversation
									</p>
								)}
							</div>
						</div>
						<div class="profile-column">
							{/* YOUR RECORD — data management */}
							<div class="section-header">
								<h2 class="t-label">Your Record</h2>
							</div>
							<div class="action-item">
								<button
									class="btn btn-action"
									onClick={() => route('/profile')}
								>
									<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
										<path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
										<path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
									</svg>
									View/Edit Record
								</button>
								<p class="t-caption action-hint">Review and edit the data behind your Profile</p>
							</div>
							<div class="action-item">
								{user?.activeSessionId ? (
									<button
										class="btn btn-action btn-action--primary"
										onClick={() => route(`/interview/${user.activeSessionId}`)}
										disabled={!user?.emailVerified}
									>
										<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
											<path d="M5 3l14 9-14 9V3z" />
										</svg>
										Resume Session
									</button>
								) : (
									<button
										class="btn btn-action btn-action--primary"
										onClick={startInterview}
										disabled={starting || !user?.canStartInterview || !user?.emailVerified}
									>
										{starting ? (
											<>
												<span class="spinner" />
												Starting...
											</>
										) : (
											<>
												<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
													<path d="M8 2C8.27614 2 8.5 2.22386 8.5 2.5V7.5H13.5C13.7761 7.5 14 7.72386 14 8C14 8.27614 13.7761 8.5 13.5 8.5H8.5V13.5C8.5 13.7761 8.27614 14 8 14C7.72386 14 7.5 13.7761 7.5 13.5V8.5H2.5C2.22386 8.5 2 8.27614 2 8C2 7.72386 2.22386 7.5 2.5 7.5H7.5V2.5C7.5 2.22386 7.72386 2 8 2Z" fill="currentColor"/>
												</svg>
												Update Record
											</>
										)}
									</button>
								)}
								<p class="t-caption action-hint">Add experience, correct details, or refine your narrative</p>
								<button
									class="private-reflection-link"
									onClick={startPrivateReflection}
									disabled={starting || !user?.emailVerified}
								>
									Start a private reflection →
								</button>
								<p class="t-caption private-reflection-hint">
									A separate space for interviews, workplace dynamics, career decisions. Never enters your profile.
								</p>
							</div>
							{!user?.canStartInterview && !user?.activeSessionId && user?.tier === 'free' && (
								<p class="t-caption upgrade-hint">
									Free tier includes one session.{' '}
									<button class="upgrade-hint-link" onClick={() => setShowUpgradePrompt(true)}>Upgrade to Pro</button> for unlimited sessions.
								</p>
							)}
						</div>
					</div>

	
					<div class="sessions-section" data-guide-section="sessions">
						{(() => {
							const recordCount = sessions.filter(s => s.mode !== 'private').length;
							const privateCount = sessions.filter(s => s.mode === 'private').length;
							const hasBothModes = recordCount > 0 && privateCount > 0;
							const filteredSessions = sessionModeFilter === 'all'
								? sessions
								: sessionModeFilter === 'private'
									? sessions.filter(s => s.mode === 'private')
									: sessions.filter(s => s.mode !== 'private');
							return (
								<>
									<div class="sessions-header">
										<h2 class="t-subtitle">
											Sessions
											{sessions.length > 0 && (
												<span class="text-muted">
													{' '}({hasBothModes
														? `${recordCount} record · ${privateCount} private`
														: sessions.length})
												</span>
											)}
										</h2>
										<p class="t-caption sessions-meta">
											{user?.tier === 'pro' ? 'Unlimited' : `${user?.interviewsRemaining ?? 0} of 1 sessions`}
										</p>
									</div>
									{hasBothModes && (
										<div class="sessions-mode-filter" role="tablist" aria-label="Filter sessions by mode">
											{(['all', 'record', 'private'] as const).map(f => (
												<button
													key={f}
													role="tab"
													aria-selected={sessionModeFilter === f}
													class={`sessions-mode-chip ${sessionModeFilter === f ? 'sessions-mode-chip--active' : ''}`}
													onClick={() => setSessionModeFilter(f)}
												>
													{f === 'all' ? 'All' : f === 'record' ? 'Record' : 'Private'}
												</button>
											))}
										</div>
									)}
									{sessions.length === 0 ? (
										<div class="empty-state">
											<div class="empty-icon">
												<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
													<path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
												</svg>
											</div>
											<p class="t-body">No sessions yet.</p>
											<p class="t-caption">Build your professional record by talking about your work — experience, skills, achievements. Takes about 15 minutes for your first session.</p>
										</div>
									) : filteredSessions.length === 0 ? (
										<div class="empty-state">
											<p class="t-caption">No {sessionModeFilter} sessions yet.</p>
										</div>
									) : (
										<div class="sessions-list">
											<div class="sessions-grid">
												{filteredSessions.slice(0, FULL_CARD_COUNT).map(session => (
										<div
											key={session.id}
											class="session-card card"
											onClick={() => route(`/interview/${session.id}`)}
										>
											<div class="session-card-header">
												<span class="session-card-title">
													{sessionLabel(session)}
												</span>
												<div class="session-card-header-right">
													<span class={`status-label status-${session.status === 'in_progress' ? 'warning' : 'success'}`}>
														{session.status === 'in_progress' ? 'IN PROGRESS' : 'COMPLETE'}
													</span>
													{session.status === 'in_progress' && (
														<button
															class="session-remove-btn"
															onClick={(e) => abandonSession(session.id, e)}
															title="Remove session"
														>
															<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
																<path d="M18 6L6 18M6 6l12 12" />
															</svg>
														</button>
													)}
												</div>
											</div>
											<p class="t-caption session-meta">
												Started {formatDate(session.startedAt)}
												{session.lastActiveAt !== session.startedAt && (
													<>{'\u00A0\u00A0\u00A0\u00A0'}Last active {formatDate(session.lastActiveAt)}</>
												)}
											</p>
											{session.sessionSummary?.objective && session.status === 'completed' && (
												<p class="session-objective">{session.sessionSummary.objective}</p>
											)}
											{session.sessionSummary?.suggestedNext && session.status === 'completed' && session.mode !== 'private' && (
												<p class="session-suggested-next">{session.sessionSummary.suggestedNext}</p>
											)}
											{session.mode === 'private' && decisionTypeLabel(session.reflectionMetadata) && (
												<div class="reflection-meta">
													<span class="reflection-decision-chip">{decisionTypeLabel(session.reflectionMetadata)}</span>
													{session.reflectionMetadata?.entitiesReferenced.slice(0, 4).map(e => (
														<span key={`${e.type}:${e.name}`} class="reflection-entity-chip">{e.name}</span>
													))}
												</div>
											)}
											<div class="session-stats">
												<div class="session-stat">
													<span class="stat-num">{session.messageCount}</span>
													<span class="t-label">Messages</span>
												</div>
												{session.mode !== 'private' && (
													<div class="session-stat">
														<span class="stat-num">{session.pendingClaimCount}</span>
														<span class="t-label">Pending Claims</span>
													</div>
												)}
											</div>
										</div>
									))}
								</div>
								{filteredSessions.length > FULL_CARD_COUNT && (
									<>
										{showAllSessions && (
											<div class="sessions-grid">
												{filteredSessions.slice(FULL_CARD_COUNT).map(session => (
													<div
														key={session.id}
														class="session-compact card"
														onClick={() => route(`/interview/${session.id}`)}
													>
														<div class="session-card-header">
															<span class="session-card-title">
																{sessionLabel(session)}
															</span>
															<div class="session-card-header-right">
																<span class={`status-label status-${session.status === 'in_progress' ? 'warning' : 'success'}`}>
																	{session.status === 'in_progress' ? 'IN PROGRESS' : 'COMPLETE'}
																</span>
																{session.status === 'in_progress' && (
																	<button
																		class="session-remove-btn session-remove-btn--row"
																		onClick={(e) => abandonSession(session.id, e)}
																		title="Remove session"
																	>
																		<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
																			<path d="M18 6L6 18M6 6l12 12" />
																		</svg>
																	</button>
																)}
															</div>
														</div>
														<p class="t-caption text-muted" style="margin: 0;">
															{formatDate(session.startedAt)}{'\u00A0\u00A0\u00A0\u00A0'}{session.messageCount} Messages
															{session.mode === 'private' && decisionTypeLabel(session.reflectionMetadata) && (
																<>{'\u00A0\u00A0\u00A0\u00A0'}{decisionTypeLabel(session.reflectionMetadata)}</>
															)}
														</p>
													</div>
												))}
											</div>
										)}
										<button class="show-more-btn" onClick={() => setShowAllSessions(!showAllSessions)}>
											{showAllSessions ? '\u2303 Show Less \u2303' : `\u2304 Show More (${filteredSessions.length - FULL_CARD_COUNT}) \u2304`}
										</button>
									</>
								)}
							</div>
										)}
									</>
								);
							})()}
					</div>

					{applications.length > 0 && (
						<div class="apps-section">
							<div class="apps-header">
								<h2 class="t-subtitle">Applications<span class="text-muted"> ({applications.length})</span></h2>
								<p class="t-caption apps-meta">
									{applications.filter(a => ['considering', 'applied', 'interviewing'].includes(a.status)).length} Active
									{'\u00A0\u00A0\u00A0'}
									{applications.filter(a => a.status === 'rejected').length} Rejected
								</p>
							</div>
							<div class="apps-grid">
								{applications.map(app => {
									const expanded = !!expandedApps[app.id];
									const isEditing = editingNotes[app.id] !== undefined;
									return (
										<div
											key={app.id}
											class="app-card card"
											onClick={() => setExpandedApps(prev => ({ ...prev, [app.id]: !expanded }))}
										>
											<div class="app-card-header">
												<div class="app-card-title-row">
													<span class="app-card-company">{app.company}</span>
													{app.role && <span class="app-card-role">{app.role}</span>}
												</div>
												<div class="app-card-header-right">
													<span class={`status-label status-${appStatusColor(app.status)}`}>
														{appStatusLabel(app.status)}
													</span>
													<span class={`report-chevron ${expanded ? 'report-chevron--open' : ''}`}>
														<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
															<path d="M6 9l6 6 6-6" />
														</svg>
													</span>
												</div>
											</div>
											<div class="app-card-meta">
												<span class="t-caption">{formatDate(app.updatedAt)}</span>
												{app.url && (
													<a
														href={app.url}
														target="_blank"
														rel="noopener noreferrer"
														class="app-card-url"
														onClick={(e) => e.stopPropagation()}
													>
														{(() => { try { return new URL(app.url).hostname.replace('www.', ''); } catch { return 'Link'; } })()}
													</a>
												)}
											</div>
											{app.notes && !expanded && (
												<p class="app-card-notes-preview">{app.notes}</p>
											)}
											{expanded && (
												<div class="app-card-expanded" onClick={(e) => e.stopPropagation()}>
													<div class="app-status-control">
														<label class="t-caption">Status</label>
														<select
															class="app-status-select"
															value={app.status}
															onChange={(e) => updateApplicationStatus(app.id, (e.target as HTMLSelectElement).value as ApplicationStatus)}
															disabled={savingApp === app.id}
														>
															<option value="considering">Considering</option>
															<option value="applied">Applied</option>
															<option value="interviewing">Interviewing</option>
															<option value="feedback_received">Feedback Received</option>
															<option value="rejected">Rejected</option>
															<option value="accepted">Accepted</option>
															<option value="declined">Declined</option>
															<option value="withdrawn">Withdrawn</option>
														</select>
													</div>
													<div class="app-notes-control">
														<label class="t-caption">Notes</label>
														<textarea
															class="app-notes-textarea"
															placeholder="Interview feedback, observations, next steps..."
															value={isEditing ? editingNotes[app.id] : (app.notes || '')}
															onFocus={() => {
																if (!isEditing) setEditingNotes(prev => ({ ...prev, [app.id]: app.notes || '' }));
															}}
															onInput={(e) => setEditingNotes(prev => ({ ...prev, [app.id]: (e.target as HTMLTextAreaElement).value }))}
														/>
														{isEditing && editingNotes[app.id] !== (app.notes || '') && (
															<button
																class="btn app-notes-save"
																onClick={() => saveApplicationNotes(app.id)}
																disabled={savingApp === app.id}
															>
																{savingApp === app.id ? 'Saving...' : 'Save'}
															</button>
														)}
													</div>
												</div>
											)}
										</div>
									);
								})}
							</div>
						</div>
					)}

					{reports.length > 0 && user?.tier !== 'pro' && (() => {
						const allTopics = Array.from(new Set(
							reports.flatMap(r => r.debrief?.keyInterests || r.topicsExplored || [])
						)).slice(0, 6);
						const allGaps = Array.from(new Set(
							reports.flatMap(r => r.debrief?.coverageGaps || [])
						)).slice(0, 3);
						return (
							<div class="reports-section" data-guide-section="reports">
								<div class="reports-header">
									<h2 class="t-subtitle">Engagements<span class="text-muted"> ({reports.length})</span></h2>
								</div>
								<div class="engagement-teaser card">
									{allTopics.length > 0 && (
										<div class="teaser-row">
											<span class="teaser-label">Topics</span>
											<div class="report-topics">
												{allTopics.map((t: string) => (
													<span key={t} class="topic-tag">{t}</span>
												))}
											</div>
										</div>
									)}
									{allGaps.length > 0 && (
										<div class="teaser-row">
											<span class="teaser-label">Thin coverage</span>
											<span class="teaser-gaps">{allGaps.join(', ')}</span>
										</div>
									)}
									<a href="/settings" class="teaser-upgrade">See who's asking and why &rarr;</a>
								</div>
							</div>
						);
					})()}

					{reports.length > 0 && user?.tier === 'pro' && (
						<div class="reports-section" data-guide-section="reports">
							<div class="reports-header">
								<h2 class="t-subtitle">Engagements<span class="text-muted"> ({reports.length})</span></h2>
							</div>
							<div class="reports-list">
								<div class="reports-grid">
									{reports.slice(0, FULL_CARD_COUNT).map(report => {
										const role = report.debrief?.classification.role || report.visitorRole || '';
										const quality = report.debrief?.classification.quality || report.visitorQuality || 'unknown';
										const topics = report.debrief?.keyInterests || report.topicsExplored || [];
										const expanded = expandedReports[report.id] !== false;
										return (
											<div
												key={report.id}
												class="report-card card"
												onClick={() => setExpandedReports(prev => ({ ...prev, [report.id]: !expanded }))}
											>
												<div class="report-card-header">
													<span class="role-label">
														{role ? formatRole(role).toUpperCase() : ''}
													</span>
													<div class="report-card-header-right">
														<span class={`status-label status-${qualityToStatus(quality)}`}>{quality.toUpperCase()}</span>
														<span class={`report-chevron ${expanded ? 'report-chevron--open' : ''}`}>
															<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
																<path d="M6 9l6 6 6-6" />
															</svg>
														</span>
													</div>
												</div>
												<span class="report-card-visitor">
													{report.debrief?.visitorSummary
														? report.debrief.visitorSummary.length > 80
															? report.debrief.visitorSummary.slice(0, 80) + '...'
															: report.debrief.visitorSummary
														: report.visitorCompany || 'Anonymous'}
												</span>
												<div class="report-card-meta">
													<span class="t-caption">{report.messageCount} Messages</span>
													<span class="t-caption">{formatDate(report.lastActiveAt)}</span>
													{report.contactRequested && (
														<span class="badge badge-primary">Contact</span>
													)}
												</div>
												{expanded && (
													<>
														{report.debrief && (
															<>
																<p class="report-summary">{report.debrief.visitorSummary}</p>
																<p class="report-reasoning">{report.debrief.classification.reasoning}</p>
																<p class="report-action">{report.debrief.recommendedAction}</p>
															</>
														)}
														{topics.length > 0 && (
															<div class="report-topics">
																{topics.slice(0, 8).map((t: string) => (
																	<span key={t} class="topic-tag">{t}</span>
																))}
															</div>
														)}
													</>
												)}
											</div>
										);
									})}
								</div>
								{reports.length > FULL_CARD_COUNT && (
									<>
										{showAllReports && (
											<div class="reports-grid">
												{reports.slice(FULL_CARD_COUNT).map(report => {
													const role = report.debrief?.classification.role || report.visitorRole || '';
													const quality = report.debrief?.classification.quality || report.visitorQuality || 'unknown';
													const topics = report.debrief?.keyInterests || report.topicsExplored || [];
													const expanded = !!expandedReports[report.id];
													return (
														<div
															key={report.id}
															class="report-card card"
															onClick={() => setExpandedReports(prev => ({ ...prev, [report.id]: !expanded }))}
														>
															<div class="report-card-header">
																<span class="role-label">
																	{role ? formatRole(role).toUpperCase() : ''}
																</span>
																<div class="report-card-header-right">
																	<span class={`status-label status-${qualityToStatus(quality)}`}>{quality.toUpperCase()}</span>
																	<span class={`report-chevron ${expanded ? 'report-chevron--open' : ''}`}>
																		<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
																			<path d="M6 9l6 6 6-6" />
																		</svg>
																	</span>
																</div>
															</div>
															<span class="report-card-visitor">
																{report.debrief?.visitorSummary
																	? report.debrief.visitorSummary.length > 80
																		? report.debrief.visitorSummary.slice(0, 80) + '...'
																		: report.debrief.visitorSummary
																	: report.visitorCompany || 'Anonymous'}
															</span>
															<div class="report-card-meta">
																<span class="t-caption">{report.messageCount} Messages</span>
																<span class="t-caption">{formatDate(report.lastActiveAt)}</span>
																{report.contactRequested && (
																	<span class="badge badge-primary">Contact</span>
																)}
															</div>
															{expanded && (
																<>
																	{report.debrief && (
																		<>
																			<p class="report-summary">{report.debrief.visitorSummary}</p>
																			<p class="report-reasoning">{report.debrief.classification.reasoning}</p>
																			<p class="report-action">{report.debrief.recommendedAction}</p>
																		</>
																	)}
																	{topics.length > 0 && (
																		<div class="report-topics">
																			{topics.slice(0, 8).map((t: string) => (
																				<span key={t} class="topic-tag">{t}</span>
																			))}
																		</div>
																	)}
																</>
															)}
														</div>
													);
												})}
											</div>
										)}
										<button class="show-more-btn" onClick={() => setShowAllReports(!showAllReports)}>
											{showAllReports ? '\u2303 Show Less \u2303' : `\u2304 Show More (${reports.length - FULL_CARD_COUNT}) \u2304`}
										</button>
									</>
								)}
							</div>
						</div>
					)}
				</div>
			</main>

			{/* Publish Preview Modal (Session #149) — diff-and-confirm gate for all publishes */}
			{showPublishPreview && profile && (
				<PublishPreviewModal
					profile={profile as unknown as ApiProfile}
					getToken={getToken}
					onClose={() => setShowPublishPreview(false)}
					onPublished={handlePublished}
				/>
			)}

			{/* Upgrade Prompt Modal */}
			{showUpgradePrompt && (
				<div class="narrative-preview-overlay" onClick={() => { if (!upgrading) setShowUpgradePrompt(false); }}>
					<div class="upgrade-prompt" onClick={(e) => e.stopPropagation()}>
						<h2 class="upgrade-prompt-title">Your free session is complete</h2>
						<p class="upgrade-prompt-body">
							Your profile and narrative are yours to keep. Pro unlocks unlimited sessions, full engagement reports, and a custom profile URL.
						</p>
						<div class="upgrade-prompt-plans">
							<label class={`plan-option ${selectedPlan === 'monthly' ? 'plan-option--selected' : ''}`}>
								<input
									type="radio"
									name="upgrade-plan"
									value="monthly"
									checked={selectedPlan === 'monthly'}
									onChange={() => setSelectedPlan('monthly')}
								/>
								<span class="plan-option-price">$12/mo</span>
							</label>
							<label class={`plan-option ${selectedPlan === 'annual' ? 'plan-option--selected' : ''}`}>
								<input
									type="radio"
									name="upgrade-plan"
									value="annual"
									checked={selectedPlan === 'annual'}
									onChange={() => setSelectedPlan('annual')}
								/>
								<span class="plan-option-price">$120/year</span>
								<span class="plan-option-save">Save $24</span>
							</label>
						</div>
						<div class="upgrade-prompt-actions">
							<button
								class="btn btn-primary"
								onClick={handleUpgrade}
								disabled={upgrading}
							>
								{upgrading ? 'Redirecting to checkout...' : 'Upgrade to Pro'}
							</button>
							<button
								class="btn btn-ghost"
								onClick={() => setShowUpgradePrompt(false)}
								disabled={upgrading}
							>
								Stay on Free
							</button>
						</div>
					</div>
				</div>
			)}

			{!loading && guideState && (
				<GuideCompanion page="dashboard" userState={guideState} dismissible />
			)}

			<style>{dashboardStyles}</style>
		</div>
	);
}

function formatRole(role: string): string {
	const roles: Record<string, string> = {
		recruiter: 'Recruiter',
		hiring_manager: 'Hiring Manager',
		peer: 'Peer',
		curious: 'Curious',
	};
	return roles[role] || role;
}

function qualityToStatus(quality: string): string {
	const map: Record<string, string> = {
		high: 'success',
		medium: 'warning',
		low: 'muted',
		unknown: 'muted',
	};
	return map[quality] || 'muted';
}

function appStatusColor(status: string): string {
	const map: Record<string, string> = {
		considering: 'muted',
		applied: 'warning',
		interviewing: 'primary',
		feedback_received: 'warning',
		rejected: 'error',
		accepted: 'success',
		declined: 'muted',
		withdrawn: 'muted',
	};
	return map[status] || 'muted';
}

function appStatusLabel(status: string): string {
	const map: Record<string, string> = {
		considering: 'Considering',
		applied: 'Applied',
		interviewing: 'Interviewing',
		feedback_received: 'Feedback',
		rejected: 'Rejected',
		accepted: 'Accepted',
		declined: 'Declined',
		withdrawn: 'Withdrawn',
	};
	return map[status] || status;
}

const dashboardStyles = `
	.dashboard-header {
		padding: 1rem 0;
		background: var(--color-bg-elevated);
		border-bottom: 1px solid var(--color-border);
		position: sticky;
		top: 0;
		z-index: 100;
		transition: padding 200ms ease;
	}

	.dashboard-header--compact {
		padding: 0.5rem 0;
	}

	.dashboard-header--compact .logo-text {
		font-size: 1.125rem;
	}

	.dashboard-nav {
		display: flex;
		align-items: center;
		justify-content: space-between;
	}

	.logo {
		text-decoration: none;
	}

	.logo-text {
		font-family: var(--font-display);
		font-size: 1.5rem;
		font-weight: 700;
		color: var(--color-title);
		letter-spacing: -0.02em;
		text-decoration: none;
	}

	.nav-actions {
		display: flex;
		align-items: center;
		gap: 1rem;
	}


	.dashboard-main {
		padding: 2rem 0;
	}

	.dashboard-main > .container {
		padding-right: calc(3rem + 60px);
	}

	.dashboard-welcome {
		margin-bottom: 1.25rem;
	}

	.dashboard-welcome .t-title {
		margin-bottom: 0.5rem;
	}

	.error-banner {
		display: flex;
		align-items: center;
		justify-content: space-between;
		background: rgba(222, 46, 33, 0.1);
		border: 1px solid var(--color-error);
		color: var(--color-error);
		padding: 0.875rem 1rem;
		border-radius: var(--radius-md);
		margin-bottom: 1.5rem;
		font-size: 0.875rem;
	}

	.verification-banner {
		display: flex;
		align-items: center;
		gap: 0.75rem;
		background: rgba(245, 166, 35, 0.1);
		border: 1px solid rgba(245, 166, 35, 0.4);
		color: var(--color-warning-text);
		padding: 0.875rem 1rem;
		border-radius: var(--radius-md);
		margin-bottom: 1.5rem;
		font-size: 0.875rem;
		line-height: 1.4;
	}

	.verification-banner svg {
		flex-shrink: 0;
	}

	.profile-section {
		display: flex;
		align-items: flex-start;
		gap: 2.5rem;
		margin-bottom: 1.5rem;
	}

	.profile-column {
		flex: 1;
		min-width: 0;
		display: flex;
		flex-direction: column;
		gap: 0.75rem;
	}


	.section-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		min-height: 2rem;
	}

	.section-header .t-label {
		margin: 0;
	}

	.profile-settings-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 2rem;
		height: 2rem;
		color: var(--color-text-subtle);
		text-decoration: none;
		border-radius: 2px;
		transition: color 150ms, background 150ms;
	}

	.profile-settings-btn:hover {
		color: var(--color-text);
		background: var(--color-bg-hover);
	}

	.avatar-card {
		position: relative;
		background: var(--color-bg-elevated);
		border: 1px solid var(--color-border);
		border-radius: 2px;
		padding: 1.5rem;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 1rem;
	}

	.avatar-card__badge {
		position: absolute;
		top: 0.75rem;
		right: 0.75rem;
	}

	.profile-publish-link {
		display: inline-flex;
		align-items: center;
		gap: 0.375rem;
		font-family: var(--font-mono);
		font-size: 0.8125rem;
		color: var(--color-primary);
		text-decoration: none;
		cursor: pointer;
	}

	.profile-publish-link:hover {
		text-decoration: underline;
	}

	.profile-info-icon {
		display: inline-flex;
		align-items: center;
		color: var(--color-text-muted);
		cursor: pointer;
		text-decoration: none;
	}

	.profile-info-icon:hover {
		color: var(--color-text);
	}

	.profile-stale-hint {
		color: var(--color-warning);
		margin: 0;
		text-align: center;
	}

	.profile-update-link {
		font-family: var(--font-mono);
		font-size: 0.75rem;
		text-transform: uppercase;
		letter-spacing: 0.04em;
		color: var(--color-text-muted);
		text-decoration: none;
		cursor: pointer;
	}

	.profile-update-link:hover {
		color: var(--color-primary);
		text-decoration: underline;
	}

	.profile-avatar-lg {
		width: 120px;
		height: 120px;
		border-radius: 50%;
		overflow: hidden;
		background: var(--color-bg);
		border: 2px solid var(--color-border);
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.profile-avatar-img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.profile-avatar-initial {
		font-family: var(--font-body);
		font-size: 0.875rem;
		color: var(--color-text-subtle);
	}

	.profile-url-row {
		display: flex;
		align-items: center;
		gap: 0.5rem;
	}

	.profile-url {
		font-family: var(--font-mono);
		font-size: 0.875rem;
		color: var(--color-primary);
		text-decoration: none;
	}

	.profile-url:hover {
		text-decoration: underline;
	}

	.action-item {
		display: flex;
		flex-direction: column;
		gap: 0.375rem;
	}

	.action-hint {
		margin: 0;
	}

	.private-reflection-link {
		display: inline-flex;
		align-items: center;
		align-self: flex-start;
		margin-top: 0.25rem;
		padding: 0;
		background: none;
		border: none;
		color: var(--color-text-secondary);
		font-size: 0.8125rem;
		font-family: inherit;
		cursor: pointer;
		text-align: left;
		transition: color 0.15s;
	}

	.private-reflection-link:hover:not(:disabled) {
		color: var(--color-text);
	}

	.private-reflection-link:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.private-reflection-hint {
		margin: 0.125rem 0 0 0;
		font-size: 0.75rem;
		color: var(--color-text-subtle);
		line-height: 1.4;
	}

	.btn-action {
		padding: 0.5rem 1rem;
		white-space: nowrap;
	}

	.btn-action--primary {
		background: var(--color-text);
		color: var(--color-bg);
		border-color: var(--color-text);
	}

	.btn-action--primary:hover:not(:disabled) {
		background: var(--color-text);
		color: var(--color-bg);
		border-color: var(--color-text);
		opacity: 0.88;
	}

	.btn-action--primary:disabled {
		background: var(--color-text-muted);
		color: var(--color-bg);
		border-color: var(--color-text-muted);
	}

	.coaching-locked {
		display: flex;
		align-items: flex-start;
		gap: 0.625rem;
		padding: 0.75rem;
		background: var(--color-bg);
		border: 1px dashed var(--color-border);
		border-radius: 2px;
		color: var(--color-text-subtle);
	}

	.coaching-locked svg {
		flex-shrink: 0;
		margin-top: 0.125rem;
	}

	.coaching-locked-text {
		margin: 0;
		color: var(--color-text-muted);
		font-size: 0.8125rem;
	}

	.coaching-upgrade-link {
		color: var(--color-primary);
		text-decoration: none;
	}

	.coaching-upgrade-link:hover {
		text-decoration: underline;
	}

	.action-item--locked {
		opacity: 0.5;
	}

	.action-item--locked .btn-action {
		cursor: default;
	}

	.upgrade-hint {
		margin-top: 0.5rem;
	}

	.error-dismiss {
		background: none;
		border: none;
		color: inherit;
		cursor: pointer;
		padding: 4px;
		display: flex;
		align-items: center;
		justify-content: center;
		opacity: 0.7;
		transition: opacity 150ms;
	}

	.error-dismiss:hover {
		opacity: 1;
	}

	.spinner {
		width: 16px;
		height: 16px;
		border: 2px solid transparent;
		border-top-color: currentColor;
		border-radius: 50%;
		animation: spin 0.6s linear infinite;
	}

	@keyframes spin {
		to { transform: rotate(360deg); }
	}

	/* ── Narrative section ── */

	.narrative-section {
		margin-top: 1.5rem;
	}

	.narrative-card {
		padding: 1.25rem 1.5rem;
	}

	.narrative-card-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 0.75rem;
	}

	.narrative-card-title {
		font-family: var(--font-display);
		font-size: 1rem;
		font-weight: 400;
		color: var(--color-text);
	}

	.narrative-card-preview {
		margin: 0 0 0.75rem;
		line-height: 1.5;
	}

	.narrative-card-meta {
		display: flex;
		gap: 1rem;
		margin-bottom: 0.75rem;
	}

	.narrative-card-badges {
		display: flex;
		align-items: center;
		gap: 0.375rem;
	}

	.narrative-card-url {
		color: var(--color-primary);
		text-decoration: none;
		font-family: var(--font-mono);
	}

	.narrative-card-url:hover {
		text-decoration: underline;
	}

	.narrative-stale-hint {
		margin: 0 0 0.5rem;
		color: var(--color-warning-text);
	}

	.narrative-card-actions {
		display: flex;
		gap: 0.5rem;
		flex-wrap: wrap;
	}

	.narrative-upgrade-hint {
		margin: 0.75rem 0 0;
		color: var(--color-text-subtle);
	}

	.narrative-upgrade-hint a {
		color: var(--color-primary);
		text-decoration: none;
	}

	/* ── Narrative preview overlay ── */

	.narrative-preview-overlay {
		position: fixed;
		inset: 0;
		z-index: 1000;
		background: rgba(0, 0, 0, 0.5);
		display: flex;
		align-items: flex-start;
		justify-content: center;
		padding: 2rem;
		overflow-y: auto;
	}

	.narrative-preview-container {
		background: var(--color-bg);
		border: 1px solid var(--color-border);
		border-radius: 2px;
		max-width: 840px;
		width: 100%;
		max-height: calc(100vh - 4rem);
		display: flex;
		flex-direction: column;
	}

	.narrative-preview-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 1rem 1.5rem;
		border-bottom: 1px solid var(--color-border);
		flex-shrink: 0;
	}

	.narrative-preview-header-left {
		display: flex;
		align-items: baseline;
		gap: 0.75rem;
	}

	.narrative-preview-header-left .t-label {
		margin: 0;
	}

	.narrative-preview-header-right {
		display: flex;
		align-items: center;
		gap: 0.5rem;
	}

	.narrative-preview-scroll {
		overflow-y: auto;
		flex: 1;
	}

	.narrative-preview-scroll .narrative-container {
		max-width: none;
	}

	/* ── Publish confirmation modal ── */

	.publish-confirm-container {
		background: var(--color-bg-elevated);
		border: 1px solid var(--color-border);
		border-radius: 2px;
		max-width: 420px;
		width: 100%;
		padding: 2rem;
	}

	.publish-confirm-title {
		font-family: var(--font-display);
		font-size: 1.25rem;
		font-weight: 300;
		color: var(--color-text);
		margin: 0 0 1rem;
	}

	.publish-confirm-url {
		font-size: 0.875rem;
		color: var(--color-text-muted);
		margin: 0 0 1.5rem;
		line-height: 1.5;
	}

	.publish-confirm-url-value {
		font-family: var(--font-mono);
		font-size: 0.875rem;
		color: var(--color-primary);
	}

	.publish-confirm-checklist {
		display: flex;
		flex-direction: column;
		gap: 0.625rem;
		margin-bottom: 1.5rem;
		padding: 1rem;
		background: var(--color-bg);
		border: 1px solid var(--color-border);
		border-radius: 2px;
	}

	.publish-confirm-item {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		font-size: 0.875rem;
		color: var(--color-text);
	}

	.publish-confirm-check {
		color: var(--color-success);
		font-weight: 600;
		width: 1rem;
		text-align: center;
		flex-shrink: 0;
	}

	.publish-confirm-x {
		color: var(--color-text-subtle);
		font-weight: 600;
		width: 1rem;
		text-align: center;
		flex-shrink: 0;
	}

	.publish-confirm-change {
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		text-transform: uppercase;
		letter-spacing: 0.04em;
		color: var(--color-primary);
		text-decoration: none;
		margin-left: auto;
	}

	.publish-confirm-change:hover {
		text-decoration: underline;
	}

	.publish-confirm-actions {
		display: flex;
		gap: 0.5rem;
	}

	/* ── Upgrade prompt modal ── */

	.upgrade-prompt {
		background: var(--color-bg-elevated);
		border: 1px solid var(--color-border);
		border-radius: 2px;
		max-width: 400px;
		width: 100%;
		padding: 2rem;
	}

	.upgrade-prompt-title {
		font-family: var(--font-display);
		font-size: 1.25rem;
		font-weight: 300;
		color: var(--color-text);
		margin: 0 0 0.75rem;
	}

	.upgrade-prompt-body {
		font-size: 0.875rem;
		color: var(--color-text-muted);
		line-height: 1.6;
		margin: 0 0 1.5rem;
	}

	.upgrade-prompt-plans {
		display: flex;
		gap: 0.5rem;
		margin-bottom: 1.5rem;
	}

	.upgrade-prompt-plans .plan-option {
		flex: 1;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 0.25rem;
		padding: 0.75rem;
		border: 1px solid var(--color-border);
		border-radius: 2px;
		cursor: pointer;
		transition: border-color 0.15s;
	}

	.upgrade-prompt-plans .plan-option input {
		display: none;
	}

	.upgrade-prompt-plans .plan-option--selected {
		border-color: var(--color-primary);
		background: var(--color-bg);
	}

	.upgrade-prompt-plans .plan-option-price {
		font-family: var(--font-mono);
		font-size: 0.9375rem;
		font-weight: 500;
		color: var(--color-text);
	}

	.upgrade-prompt-plans .plan-option-save {
		font-size: 0.6875rem;
		text-transform: uppercase;
		letter-spacing: 0.04em;
		color: var(--color-success);
	}

	.upgrade-prompt-actions {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
	}

	.upgrade-prompt-actions .btn-primary {
		width: 100%;
	}

	.upgrade-prompt-actions .btn-ghost {
		width: 100%;
		text-align: center;
	}

	.upgrade-hint-link {
		background: none;
		border: none;
		padding: 0;
		font: inherit;
		color: var(--color-primary);
		cursor: pointer;
		text-decoration: none;
	}

	.upgrade-hint-link:hover {
		text-decoration: underline;
	}

	.sessions-section {
		margin-top: 1.5rem;
	}

	.sessions-header {
		display: flex;
		align-items: baseline;
		justify-content: space-between;
		margin-bottom: 1rem;
	}

	.sessions-header .t-subtitle {
		margin: 0;
	}

	.sessions-meta {
		margin: 0;
	}

	.empty-state {
		text-align: center;
		padding: 3rem;
		background: var(--color-bg-elevated);
		border: 1px solid var(--color-border);
		border-radius: 2px;
	}

	.empty-icon {
		color: var(--color-text-subtle);
		margin-bottom: 1rem;
	}

	.empty-state .t-body {
		margin-bottom: 0.25rem;
	}

	.sessions-list {
		display: flex;
		flex-direction: column;
		gap: 0.75rem;
	}

	.sessions-grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 0.75rem;
	}

	.status-label {
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		font-weight: 600;
		letter-spacing: 0.05em;
		text-transform: uppercase;
	}

	.status-label.status-success {
		color: var(--color-success);
	}

	.status-label.status-warning {
		color: var(--color-warning-text);
	}

	.status-label.status-muted {
		color: var(--color-text-subtle);
	}

	.role-label {
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		font-weight: 500;
		letter-spacing: 0.04em;
		color: var(--color-text-muted);
	}

	.session-card {
		display: flex;
		flex-direction: column;
		cursor: pointer;
		padding: 1rem;
	}

	.session-card-title {
		font-family: var(--font-display);
		font-size: 1.125rem;
		font-weight: 600;
		color: var(--color-text);
		letter-spacing: -0.01em;
	}

	.session-card-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 0.25rem;
	}

	.session-card-header-right {
		display: flex;
		align-items: center;
		gap: 0.375rem;
	}

	.session-meta {
		margin: 0 0 0.5rem;
	}

	.session-stats {
		display: flex;
		align-items: flex-end;
		gap: 1.5rem;
		margin-top: auto;
		padding-top: 0.75rem;
	}

	.session-stat {
		text-align: left;
	}

	.session-stat .stat-num {
		font-family: var(--font-display);
		font-size: 2rem;
		font-weight: 300;
		color: var(--color-text);
		display: block;
		line-height: 1.1;
	}

	.session-stat .t-label {
		margin: 0;
		font-size: 0.75rem;
	}

	.session-objective {
		font-size: 0.8125rem;
		color: var(--color-text-muted);
		line-height: 1.45;
		margin: 0.125rem 0 0;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	.session-suggested-next {
		font-size: 0.8125rem;
		color: var(--color-text);
		line-height: 1.45;
		margin: 0.375rem 0 0;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	.reflection-meta {
		display: flex;
		flex-wrap: wrap;
		gap: 0.375rem;
		margin: 0.5rem 0 0;
	}

	.reflection-decision-chip {
		font-size: 0.6875rem;
		padding: 0.1875rem 0.5rem;
		border-radius: 2px;
		background: var(--color-surface-muted, rgba(0, 0, 0, 0.04));
		color: var(--color-text);
		letter-spacing: 0.02em;
		text-transform: uppercase;
		font-weight: 500;
	}

	.reflection-entity-chip {
		font-size: 0.6875rem;
		padding: 0.1875rem 0.5rem;
		border-radius: 2px;
		color: var(--color-text-muted);
		border: 1px solid var(--color-border);
	}

	.sessions-mode-filter {
		display: flex;
		gap: 0.25rem;
		margin: 0 0 0.75rem;
	}

	.sessions-mode-chip {
		font-size: 0.75rem;
		padding: 0.25rem 0.625rem;
		border: 1px solid var(--color-border);
		background: transparent;
		color: var(--color-text-muted);
		cursor: pointer;
		border-radius: 2px;
		letter-spacing: 0.01em;
	}

	.sessions-mode-chip:hover {
		color: var(--color-text);
	}

	.sessions-mode-chip--active {
		background: var(--color-text);
		color: var(--color-background, #fff);
		border-color: var(--color-text);
	}

	.session-remove-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 1.5rem;
		height: 1.5rem;
		padding: 0;
		background: none;
		border: none;
		color: var(--color-text-subtle);
		cursor: pointer;
		border-radius: 2px;
		opacity: 0;
		transition: opacity 150ms, color 150ms, background 150ms;
	}

	.session-card:hover .session-remove-btn,
	.session-compact:hover .session-remove-btn,
	.session-remove-btn--row {
		opacity: 1;
	}

	.session-remove-btn:hover {
		color: var(--color-error);
		background: rgba(222, 46, 33, 0.08);
	}

	.session-remove-btn--row {
		flex-shrink: 0;
	}

	.session-compact {
		display: flex;
		flex-direction: column;
		padding: 0.75rem 1rem;
		cursor: pointer;
		transition: background 150ms;
	}

	.session-compact:hover {
		background: var(--color-bg-hover);
	}

	.show-more-btn {
		display: block;
		width: 100%;
		background: none;
		border: 1px solid var(--color-border);
		border-radius: 2px;
		color: var(--color-text-muted);
		font-family: var(--font-mono);
		font-size: 0.75rem;
		padding: 0.5rem 1rem;
		cursor: pointer;
		text-align: center;
		transition: color 150ms, border-color 150ms;
	}

	.show-more-btn:hover {
		color: var(--color-text);
		border-color: var(--color-text-subtle);
	}

	.badge-sm {
		display: inline-block;
		font-family: var(--font-mono);
		font-size: 0.625rem;
		text-transform: uppercase;
		letter-spacing: 0.03em;
		padding: 0.0625rem 0.375rem;
		border-radius: 2px;
		flex-shrink: 0;
	}

	.badge-sm--success {
		background: rgba(39, 174, 96, 0.1);
		color: var(--color-success);
	}

	.badge-sm--warning {
		background: rgba(245, 166, 35, 0.1);
		color: var(--color-warning-text);
	}

	.badge-sm--primary {
		background: rgba(var(--color-primary-rgb, 59, 130, 246), 0.1);
		color: var(--color-primary);
	}

	.badge-sm--default {
		background: var(--color-bg);
		color: var(--color-text-muted);
	}

	.reports-section {
		margin-top: 2rem;
	}

	.reports-header {
		display: flex;
		align-items: baseline;
		justify-content: space-between;
		margin-bottom: 1rem;
	}

	.reports-header .t-subtitle {
		margin: 0;
	}

	.reports-meta {
		margin: 0;
	}

	.engagement-teaser {
		display: flex;
		flex-direction: column;
		gap: 0.75rem;
		padding: 1.25rem;
	}

	.teaser-row {
		display: flex;
		flex-direction: column;
		gap: 0.375rem;
	}

	.teaser-label {
		font-family: var(--font-mono);
		font-size: 11px;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		color: var(--color-text-muted);
	}

	.teaser-gaps {
		font-size: 0.9375rem;
		color: var(--color-text);
	}

	.teaser-upgrade {
		font-family: var(--font-mono);
		font-size: 12px;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		color: var(--color-text-muted);
		text-decoration: none;
		margin-top: 0.25rem;
		transition: color 150ms ease;
	}

	.teaser-upgrade:hover {
		color: var(--color-text);
	}

	.reports-list {
		display: flex;
		flex-direction: column;
		gap: 0.75rem;
	}

	.reports-grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 0.75rem;
		align-items: start;
	}

	.report-card {
		display: flex;
		flex-direction: column;
		padding: 1rem;
		cursor: pointer;
		transition: background 150ms;
	}

	.report-card:hover {
		background: var(--color-bg-hover);
	}

	.report-card-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 0.125rem;
	}

	.report-card-header-right {
		display: flex;
		align-items: center;
		gap: 0.5rem;
	}

	.report-chevron {
		color: var(--color-text-subtle);
		display: flex;
		align-items: center;
		transition: transform 200ms ease;
	}

	.report-chevron--open {
		transform: rotate(180deg);
	}

	.report-card-visitor {
		font-family: var(--font-display);
		font-size: 1.125rem;
		font-weight: 600;
		color: var(--color-text);
		margin-bottom: 0.25rem;
		letter-spacing: -0.01em;
	}

	.report-card-meta {
		display: flex;
		align-items: center;
		gap: 0.75rem;
		margin-bottom: 0.375rem;
	}


	.report-details {
		display: flex;
		gap: 0.75rem;
		margin-bottom: 0.25rem;
		color: var(--color-text-muted);
	}

	.report-topics {
		display: flex;
		flex-wrap: wrap;
		gap: 0.375rem;
		margin-top: 0.5rem;
	}

	.topic-tag {
		display: inline-block;
		padding: 0.1875rem 0.5rem;
		background: var(--color-bg);
		border: 1px solid var(--color-border);
		border-radius: 3px;
		font-family: var(--font-body);
		font-size: 0.75rem;
		color: var(--color-text-muted);
	}

	.report-summary {
		font-size: 0.8125rem;
		line-height: 1.45;
		color: var(--color-text);
		margin: 0.5rem 0 0;
	}

	.report-reasoning {
		font-size: 0.75rem;
		line-height: 1.45;
		color: var(--color-text-muted);
		margin: 0.5rem 0 0;
		font-style: italic;
	}

	.report-action {
		font-size: 0.8125rem;
		line-height: 1.45;
		color: var(--color-text);
		margin: 0.5rem 0 0;
		font-weight: 500;
	}


	.apps-section {
		margin-top: 2rem;
	}

	.apps-header {
		display: flex;
		align-items: baseline;
		justify-content: space-between;
		margin-bottom: 1rem;
	}

	.apps-header .t-subtitle {
		margin: 0;
	}

	.apps-meta {
		margin: 0;
	}

	.apps-grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 0.75rem;
	}

	.app-card {
		display: flex;
		flex-direction: column;
		padding: 1rem;
		cursor: pointer;
		transition: background 150ms;
	}

	.app-card:hover {
		background: var(--color-bg-hover);
	}

	.app-card-header {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: 0.75rem;
		margin-bottom: 0.25rem;
	}

	.app-card-title-row {
		display: flex;
		flex-direction: column;
		min-width: 0;
	}

	.app-card-company {
		font-family: var(--font-display);
		font-size: 1.125rem;
		font-weight: 600;
		color: var(--color-text);
		letter-spacing: -0.01em;
		line-height: 1.2;
	}

	.app-card-role {
		font-size: 0.8125rem;
		color: var(--color-text-muted);
		margin-top: 0.125rem;
	}

	.app-card-header-right {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		flex-shrink: 0;
	}

	.app-card-meta {
		display: flex;
		align-items: center;
		gap: 0.75rem;
		margin-bottom: 0.25rem;
	}

	.app-card-url {
		font-family: var(--font-mono);
		font-size: 0.75rem;
		color: var(--color-primary);
		text-decoration: none;
	}

	.app-card-url:hover {
		text-decoration: underline;
	}

	.app-card-notes-preview {
		font-size: 0.8125rem;
		color: var(--color-text-muted);
		line-height: 1.4;
		margin: 0.25rem 0 0;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	.app-card-expanded {
		display: flex;
		flex-direction: column;
		gap: 0.75rem;
		margin-top: 0.75rem;
		padding-top: 0.75rem;
		border-top: 1px solid var(--color-border);
	}

	.app-status-control,
	.app-notes-control {
		display: flex;
		flex-direction: column;
		gap: 0.375rem;
	}

	.app-status-select {
		font-family: var(--font-body);
		font-size: 0.8125rem;
		padding: 0.375rem 0.5rem;
		border: 1px solid var(--color-border);
		border-radius: 2px;
		background: var(--color-bg);
		color: var(--color-text);
		cursor: pointer;
		max-width: 200px;
	}

	.app-status-select:focus {
		outline: none;
		border-color: var(--color-primary);
	}

	.app-notes-textarea {
		font-family: var(--font-body);
		font-size: 0.8125rem;
		line-height: 1.45;
		padding: 0.5rem;
		border: 1px solid var(--color-border);
		border-radius: 2px;
		background: var(--color-bg);
		color: var(--color-text);
		resize: vertical;
		min-height: 3.5rem;
	}

	.app-notes-textarea::placeholder {
		color: var(--color-text-subtle);
	}

	.app-notes-textarea:focus {
		outline: none;
		border-color: var(--color-primary);
	}

	.app-notes-save {
		align-self: flex-end;
		padding: 0.25rem 0.75rem;
		font-size: 0.75rem;
	}

	.status-label.status-primary {
		color: var(--color-primary);
	}

	.status-label.status-error {
		color: var(--color-error);
	}

	@media (max-width: 768px) {
		.profile-section {
			flex-direction: column;
			gap: 1.5rem;
		}

		.profile-column {
			width: 100%;
		}

		.avatar-card {
			width: 100%;
		}

		.btn-action {
			width: 100%;
		}

		.sessions-grid,
		.reports-grid,
		.apps-grid {
			grid-template-columns: 1fr;
		}

		.session-remove-btn {
			opacity: 1;
		}

		.session-row,
		.report-row {
			flex-wrap: wrap;
		}

		.dashboard-main > .container {
			padding-right: 1.25rem;
		}
	}
`;
